import RangeLimits from '@scripts/components/RangeLimits';
import FormPartial from '@scripts/forms/FormPartial';
import IcrasHelpers from '@scripts/helpers';

/**
 * "BasicDetails" form partial
 */
export default class extends FormPartial {


	/**
   * Normal limit
   *
   * @private
   *
   * @type {number}
   */
  #normalWeightLimit = 18.5;

	/**
   * South Asian Overweight limit
   *
   * @private
   *
   * @type {number}
   */
  #asianOverweightLimit = 25;

	/**
   * South Asian Obese limit
   *
   * @private
   *
   * @type {number}
   */
	#asianObeseLimit = 30;

	/**
   * Rest of World Overweight limit
   *
   * @private
   *
   * @type {number}
   */
	#restWorldOverweightLimit = 27;
	
	/**
   * Rest of World Obese limit
   *
   * @private
   *
   * @type {number}
   */
	#restWorldObeseLimit = 30;

	/**
   * If the components are being refreshed
   *
   * @private
   *
   * @type {bool}
   */
  #isRefresh = false;

  /**
   * Patient gender
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #gender;

	/**
   * Patient age
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #age;

	/**
   * Family History 
   *
   * @private
   *
   * @type {HTMLLabelElement}
   */
  #fh;

	/**
   * Family History diagnosis  age
   *
   * @private
   *
   * @type {HTMLSelectElement}
   */
  #fhDiagnosisAge;


	/**
   * Origin switcher for BMI
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #originSwitch;

	/**
   * Origin
   *
   * @private
   *
   * @type {string}
  */
  #origin;

	/**
   * Patient height
   *
   * @private
   *
   * @type {RangeLimits}
  */
  #height;
	
	/**
   * Patient weight
   *
   * @private
   *
   * @type {RangeLimits}
  */
  #weight;

	/**
   * Body Mass Index
   *
   * @private
   *
   * @type {HTMLElement}
  */
  #bmi;

	/**
   * Body Mass Index Value
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #bmiValue;

	/**
   * Body Mass Index Status (Obese / Overweight )
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #bmiStatus;


  constructor(form) {
    super(form);

		this.#isRefresh = false;

    this.#gender = this._form.element.querySelector('#icrasBasicDetailsGender');
		this.#age = new RangeLimits(
      this._form.element.querySelector('#icrasBasicDetailsAge')
    );
		this.#fh = this._form.element.querySelector('#icrasFamilyHistory');
		this.#fhDiagnosisAge = this._form.element.querySelector('#icrasFamilyHistoryDiagnosisAge');

		this.#originSwitch = this._form.element.querySelector(
      '#icrasOriginSwitch'
    );
		this.#origin = this.#originSwitch.querySelector('input:checked').value;

		this.#height = new RangeLimits(
      this._form.element.querySelector('#icrasBasicDetailsHeight')
    );
		this.#weight = new RangeLimits(
      this._form.element.querySelector('#icrasBasicDetailsWeight')
    );

		this.#bmi = this._form.element.querySelector('#icrasBasicDetailBMIResult');
		this.#bmiStatus = this._form.element.querySelector('#icrasBasicDetailBMIStatus');
  }
 

	/**
   * Compute of Bmi if the values are valid
   *
   * @private
   *
   * @return {void}
   */
  #computeBmi() {
    // disabled during refresh
    if (this.#isRefresh) {
      return;
    }

    // reset
    this._resetMessages();
    this.#bmi.innerText = '';
    this.#bmiValue = null;
		this.#bmiStatus.innerText = ''; // reset le statut

		// compute Bmi: weight (kg) / height (meter) ^2 = BMI
		let weight = this.#weight.getValue();
		let height = this.#height.getValue();

		// origin value (south_asian ou rest_world)
		let origin = this.#origin;

		this.#bmiValue = (weight / Math.pow(height / 100, 2)).toFixed(2);
		
		if (this.#bmiValue > 1 && this.#bmiValue < 100) {
		this.#bmi.innerText = this.#bmiValue;

		// convert bmi for comparing
		const bmi = parseFloat(this.#bmiValue);

    const isAsian = origin === 'south_asian';
		const overweightLimit = isAsian ? this.#asianOverweightLimit : this.#restWorldOverweightLimit;
		const obeseLimit = isAsian ? this.#asianObeseLimit : this.#restWorldObeseLimit;
		const normalLimit = isAsian ? this.#normalWeightLimit : this.#normalWeightLimit;

    // calcul du statut
    let status = IcrasHelpers.label('underweight');
    if (bmi > obeseLimit) {
      status = IcrasHelpers.label('obese');
    } else if (bmi > overweightLimit) {
      status = IcrasHelpers.label('overweight');
    } else if (bmi > normalLimit) {
      status = IcrasHelpers.label('normal');
    }

    // affichage du statut
    this.#bmiStatus.innerText = status;
		}
		else {
			this.#bmi.innerText = '-';
			this.#bmiStatus.innerText = '';
		}
  }

  initialize() {
    // BMI
    [this.#height, this.#weight].forEach((range) =>
      range.on('updateValue', () => this.#computeBmi())
    );

		// Change Origin
    this.#originSwitch.addEventListener('change', (e) => {
			this.#origin = e.target.value;

      this.#computeBmi();
		});

		// Exécuter le calcul si le formulaire est autocomplété
		if (this._form.element.classList.contains('is-autocompleted')) {
			this.#computeBmi();
		}
  }

  validate() {
    this._resetMessages();

    let errors = [];

    // Gender
    if (!this.#gender.querySelector('input[type="radio"]:checked')) {
      errors.push(IcrasHelpers.error('gender_required'));
    }

		const fh = this.#fhDiagnosisAge.closest(".icras-group");
		if (fh && fh.classList.contains("icras-active")) {
			if (!this.#fhDiagnosisAge.value) {
				errors.push(IcrasHelpers.error('fh_age_required'));
			}
		}

		const bmi = this.#bmi.innerText;
		if (bmi == '-' || bmi == '') {
			errors.push(IcrasHelpers.error('bmi-not-pertinent'));
		}

    this.addErrors(errors);

    return super.validate();
  }

  reset() {
    super.reset();

    // Gender
    this.#gender
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((input) => {
        input.checked = false;
      });

		/**
     * Reset Toggle components
     *
     * Family History
     * Diabetes mellitus
     */
    [this.#fh].forEach((label) => {
      label
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach((input) => {
          input.checked = false;
          // for toggle
          input.dispatchEvent(new Event('change'));
        });
    });

		[this.#age,this.#height, this.#weight].forEach(
      (range) => range.reset()
    );

		// Age
    this.#fhDiagnosisAge.value = this.#fhDiagnosisAge.querySelector('option').value;
  }

	getAgeSliderValue() {
		return this.#age.getValue();
	}

	onAgeChange(value) {
		this.#age.on('updateValue', value);
	}

	offAgeChange(value) {
		this.#age.off('updateValue', value);
	}

	getGenderValue() {
	  const checked = this.#gender.querySelector('input[type="radio"]:checked');
  	return checked ? checked.value : null;
	}
	
	onGenderChange(value) {
		this.#gender.querySelectorAll('input[type="radio"]').forEach((input) => {
			input.addEventListener('change', value);
		});
	}

	offGenderChange(value) {
		this.#gender.querySelectorAll('input[type="radio"]').forEach((input) => {
			input.removeEventListener('change', value);
		});
	}	
}
